import React, { useState, useEffect } from 'react';
import { Col, Row } from 'reactstrap';
import { withStyles } from '@material-ui/core';
import * as Constants from '../Pages/Constants';
import { FormattedMessage, useIntl } from 'react-intl';
import DataServiceSQL from '../DataService/DataServiceSQL';

const fmtCN = (subject) => {
  if (!subject) return 'n/a';
  if (typeof subject === 'string') return subject;
  return subject.CN || JSON.stringify(subject);
};

const fmtBytes = (n) => {
  if (n == null) return 'n/a';
  if (n < 1024) return n + ' B';
  return (n / 1024).toFixed(1) + ' KB';
};

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : 'n/a');

const KV = ({ label, children }) => (
  <Row style={{ marginBottom: 4 }}>
    <Col xs={4} style={{ color: '#555' }}>{label}</Col>
    <Col xs={8} style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.85em' }}>{children}</Col>
  </Row>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 16 }}>
    <h5 style={{ borderBottom: '1px solid #ddd', paddingBottom: 4, marginBottom: 8 }}>{title}</h5>
    {children}
  </div>
);

const CertificateInfoPopup = (props) => {
  const { classes } = props;
  const intl = useIntl();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    setActionMessage('');
    try {
      const res = await DataServiceSQL.getSslInfo();
      setData(res.data);
    } catch (e) {
      setError(intl.formatMessage({ id: 'certificate.error', defaultMessage: 'Failed to fetch' }) + ': ' + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCheckNow = async () => {
    setLoading(true);
    setActionMessage('');
    setError('');
    try {
      const res = await DataServiceSQL.triggerSslCheck();
      const reason = res.data && (res.data.reason || (res.data.changed === false ? 'unchanged' : 'unknown'));
      const map = {
        initial:   intl.formatMessage({ id: 'certificate.checkResult.initial' }),
        updated:   intl.formatMessage({ id: 'certificate.checkResult.updated' }),
        unchanged: intl.formatMessage({ id: 'certificate.checkResult.unchanged' }),
      };
      setActionMessage(map[reason] || JSON.stringify(res.data));
      const refreshed = await DataServiceSQL.getSslInfo();
      setData(refreshed.data);
    } catch (e) {
      setError(intl.formatMessage({ id: 'certificate.error' }) + ': ' + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const fs = data && data.filesystem;
  const live = data && data.live;
  const drift = data && data.drift;

  return (
    <div className={classes.popup_background}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div className="submit-form" style={{ maxHeight: '85vh', overflowY: 'auto', maxWidth: 800, margin: '0 auto' }}>
          <h3 style={{ textAlign: 'center', marginBottom: 16 }}>
            <FormattedMessage id="certificate.title" defaultMessage="SSL Certificate" />
          </h3>

          {loading && (
            <div style={{ textAlign: 'center', margin: 12 }}>
              <FormattedMessage id="certificate.loading" defaultMessage="Loading..." />
            </div>
          )}

          {error && (
            <div style={{ color: '#b00', background: '#fee', padding: 8, borderRadius: 4, marginBottom: 12 }}>
              {error}
            </div>
          )}

          {actionMessage && (
            <div style={{ color: '#060', background: '#efe', padding: 8, borderRadius: 4, marginBottom: 12 }}>
              {actionMessage}
            </div>
          )}

          {data && (
            <>
              <Section title={<FormattedMessage id="certificate.section.drift" defaultMessage="Sync status" />}>
                {drift && drift.diskChainHash && drift.liveChainHash ? (
                  drift.isDrifted ? (
                    <div style={{ color: '#b00', fontWeight: 'bold' }}>
                      ⚠ <FormattedMessage id="certificate.drift.ko" defaultMessage="Out of sync" />
                    </div>
                  ) : (
                    <div style={{ color: '#060', fontWeight: 'bold' }}>
                      ✓ <FormattedMessage id="certificate.drift.ok" defaultMessage="In sync" />
                    </div>
                  )
                ) : (
                  <div style={{ color: '#888' }}>n/a</div>
                )}
                <KV label={intl.formatMessage({ id: 'certificate.fingerprint' }) + ' (disk chain)'}>
                  {drift && drift.diskChainHash || 'n/a'}
                </KV>
                <KV label={intl.formatMessage({ id: 'certificate.fingerprint' }) + ' (live chain)'}>
                  {drift && drift.liveChainHash || 'n/a'}
                </KV>
              </Section>

              <Section title={<FormattedMessage id="certificate.section.fs" defaultMessage="File system" />}>
                <KV label={<FormattedMessage id="certificate.path" defaultMessage="Path" />}>{fs && fs.path}</KV>
                <KV label={<FormattedMessage id="certificate.mtime" defaultMessage="Last modified" />}>{fmtDate(fs && fs.mtime)}</KV>
                <KV label={<FormattedMessage id="certificate.size" defaultMessage="Size" />}>{fmtBytes(fs && fs.fileSize)}</KV>
                {fs && fs.metadata && (
                  <>
                    <KV label={<FormattedMessage id="certificate.subject" defaultMessage="Subject" />}>{fmtCN(fs.metadata.subject)}</KV>
                    <KV label={<FormattedMessage id="certificate.fingerprint" defaultMessage="Fingerprint" />}>{fs.metadata.fingerprint || 'n/a'}</KV>
                    <KV label={<FormattedMessage id="certificate.validFrom" defaultMessage="Valid from" />}>{fs.metadata.validFrom || 'n/a'}</KV>
                    <KV label={<FormattedMessage id="certificate.validTo" defaultMessage="Valid until" />}>{fs.metadata.validTo || 'n/a'}</KV>
                    <KV label={<FormattedMessage id="certificate.chain" defaultMessage="Chain" />}>
                      {(fs.metadata.chain || []).map(certificate => fmtCN(certificate.subject)).join(' → ') || 'n/a'}
                    </KV>
                  </>
                )}
              </Section>

              <Section title={<FormattedMessage id="certificate.section.live" defaultMessage="Live HTTPS response" />}>
                {data.liveError && (
                  <div style={{ color: '#b00' }}>{data.liveError}</div>
                )}
                {live && (
                  <>
                    <KV label={<FormattedMessage id="certificate.leaf" defaultMessage="Server certificate" />}>
                      {fmtCN(live.subject)}
                    </KV>
                    <KV label={intl.formatMessage({ id: 'certificate.fingerprint' }) + ' (server)'}>
                      {live.fingerprint || 'n/a'}
                    </KV>
                    <KV label={<FormattedMessage id="certificate.validFrom" defaultMessage="Valid from" />}>{live.validFrom || 'n/a'}</KV>
                    <KV label={<FormattedMessage id="certificate.validTo" defaultMessage="Valid until" />}>{live.validTo || 'n/a'}</KV>
                    <KV label={<FormattedMessage id="certificate.anchor" defaultMessage="Gateway root (/crt)" />}>
                      {fmtCN(live.gatewayCertificate && live.gatewayCertificate.subject)}
                    </KV>
                    <KV label={intl.formatMessage({ id: 'certificate.fingerprint' }) + ' (gateway root)'}>
                      {live.gatewayCertificate && live.gatewayCertificate.fingerprint || 'n/a'}
                    </KV>
                    <KV label={<FormattedMessage id="certificate.chain" defaultMessage="Chain" />}>
                      {(live.chainSubjects || []).join(' → ')}
                    </KV>
                  </>
                )}
              </Section>
            </>
          )}

          <div style={{ textAlign: 'center', marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button onClick={load} className="btn btn-secondary" disabled={loading}>
              <FormattedMessage id="certificate.refresh" defaultMessage="Refresh" />
            </button>
            <button onClick={handleCheckNow} className="btn btn-primary" disabled={loading}>
              <FormattedMessage id="certificate.checkNow" defaultMessage="Force check" />
            </button>
            <button onClick={props.cancelPopup} className="btn btn-danger">
              <FormattedMessage id="button.close" defaultMessage="Close" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withStyles(Constants.styles)(CertificateInfoPopup);
